import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Sparkles, BookOpenText, ShieldCheck } from "lucide-react";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen relative">
      <div className="ascend-atmosphere" />
      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-4 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="brand-home-link">
          <img
            src="/logo-128.png?v=dragon-padded"
            alt=""
            width={36}
            height={36}
            className="w-9 h-9 rounded-xl select-none soft-pulse"
            draggable={false}
            data-testid="brand-logo"
          />
          <div className="leading-tight">
            <div className="font-display text-2xl" style={{ color: "var(--text)" }}>Ascend</div>
            <div className="font-hand text-sm -mt-1" style={{ color: "var(--accent)" }}>your quiet climb</div>
          </div>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          <NavItem to="/dashboard" testid="nav-dashboard">Dashboard</NavItem>
          <NavItem to="/timetable" testid="nav-timetable">Timetable</NavItem>
          <NavItem to="/tasks" testid="nav-tasks">Tasks</NavItem>
          <NavItem to="/journal" testid="nav-journal">Journal</NavItem>
          <NavItem to="/news" testid="nav-news" hideOnSmall>News</NavItem>
          <NavItem to="/gratitude" testid="nav-gratitude" hideOnSmall>Gratitude</NavItem>
          <NavItem to="/dreamboard" testid="nav-dreamboard" hideOnSmall>Board</NavItem>
          <NavItem to="/music" testid="nav-music" hideOnSmall>Music</NavItem>
          <NavItem to="/share" testid="nav-share" hideOnSmall>Share</NavItem>
          <NavItem to="/studio" testid="nav-studio" hideOnSmall>Studio</NavItem>
          <NavItem to="/subjects" testid="nav-subjects" hideOnSmall>Subjects</NavItem>
          {user && typeof user === "object" && user.is_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `hidden md:inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-[var(--surface)] shadow-sm" : "hover:bg-[var(--surface-2)]"
                }`
              }
              style={({ isActive }) => ({ color: isActive ? "var(--primary)" : "var(--text)" })}
              aria-label="Admin"
            >
              <ShieldCheck size={14} strokeWidth={1.5} />
              Admin
            </NavLink>
          )}
          {user && typeof user === "object" && (
            <button
              onClick={doLogout}
              className="btn-ghost flex items-center gap-2 text-sm"
              data-testid="logout-btn"
              aria-label="Log out"
            >
              <LogOut size={16} strokeWidth={1.5} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          )}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">{children}</main>

      <footer className="max-w-6xl mx-auto px-5 sm:px-8 pb-8 text-center">
        <div className="font-hand text-base" style={{ color: "var(--text-muted)" }}>
          <BookOpenText size={14} strokeWidth={1.5} className="inline mr-1" />
          one page, one page, one page.
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, children, testid, hideOnSmall = false }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `${hideOnSmall ? "hidden md:inline-block " : ""}px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
          isActive ? "bg-[var(--surface)] shadow-sm" : "hover:bg-[var(--surface-2)]"
        }`
      }
      style={({ isActive }) => ({
        color: isActive ? "var(--primary)" : "var(--text)",
      })}
    >
      {children}
    </NavLink>
  );
}
